import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styles from './messenger.module.css';
import { API_URL } from '../Utils/api';
import { FaSearch, FaCircle, FaInbox, FaEdit } from 'react-icons/fa';
import Menu from './Menu';
import useSocket from '../hooks/useSocket';
import { useNavigate } from 'react-router-dom';

const Messenger = ({ onOpenChat, setActive, active, pp, showChat }) => {
  const [conversations, setConversations] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [isMobile,      setIsMobile]      = useState(false);
  const [typingUsers,   setTypingUsers]   = useState(new Set());

  const token    = useMemo(() => localStorage.getItem('token'), []);
  const userId   = useMemo(() => localStorage.getItem('userId'), []);
  const navigate = useNavigate();
  const socket   = useSocket();

  // Anti double-call
  const fetchingRef = useRef(false);

  /* ── Fetch conversations ── */
  const fetchConversations = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      setError(null);
      const res = await fetch(`${API_URL}/conversations_list`, {
        headers: { Authorization: `Bearer${token}` }
      });
      if (!res.ok) throw new Error('Erreur de chargement');
      const data = await res.json();
      setConversations(data || []);
    } catch (err) {
      setError(err.message);
      setConversations([]);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [token]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  /* ── Responsive ── */
  useEffect(() => {
    const check = () => setIsMobile(/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ── Socket temps réel ── */
  useEffect(() => {
    if (!socket) return;

    const onNewMsg = (msg) => {
      // ✅ Mise à jour optimiste sans refetch réseau
      setConversations(prev => {
        const otherId = msg.senderId === userId ? msg.receiverId : msg.senderId;
        const existing = prev.findIndex(c =>
          c.conversationWith?.id?.toString() === otherId?.toString()
        );
        const updatedConv = {
          conversationWith: existing >= 0
            ? prev[existing].conversationWith
            : { id: otherId, username: '...', userPP: null },
          lastMessage: {
            id       : msg._id,
            text     : msg.message,
            sender   : msg.senderId,
            createdAt: msg.createdAt,
            // ✅ Badge unread seulement si c'est un message reçu (pas envoyé par moi)
            unread   : msg.senderId !== userId ? 1 : 0,
          }
        };

        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = updatedConv;
          return updated;
        }
        // Nouvelle conversation — fetch complet pour avoir les infos user
        fetchConversations();
        return prev;
      });
      // Retirer de "en train d'écrire" quand le message est arrivé
      setTypingUsers(prev => {
        const next = new Set(prev);
        next.delete(msg.senderId);
        return next;
      });
    };

    const onTyping = (data) => {
      setTypingUsers(prev => {
        const next = new Set(prev);
        next.add(data.senderId);
        return next;
      });
    };

    const onStopTyping = (data) => {
      setTypingUsers(prev => {
        const next = new Set(prev);
        next.delete(data.senderId);
        return next;
      });
    };

    const onUserOnline = (id) => {
      setConversations(prev => prev.map(c => 
        String(c.conversationWith?.id) === String(id) || String(c.conversationWith?._id) === String(id)
          ? { ...c, conversationWith: { ...c.conversationWith, socketId: "online" } }
          : c
      ));
    };

    const onUserOffline = (id) => {
      setConversations(prev => prev.map(c => 
        String(c.conversationWith?.id) === String(id) || String(c.conversationWith?._id) === String(id)
          ? { ...c, conversationWith: { ...c.conversationWith, socketId: null } }
          : c
      ));
    };

    // ✅ Écoute les événements émis par le backend
    socket.on('newMsg',   onNewMsg);
    socket.on('message',  () => fetchConversations()); // fallback
    socket.on('typing', onTyping);
    socket.on('stopTyping', onStopTyping);
    socket.on('userOnline', onUserOnline);
    socket.on('userOffline', onUserOffline);

    return () => {
      socket.off('newMsg',  onNewMsg);
      socket.off('message', fetchConversations);
      socket.off('typing', onTyping);
      socket.off('stopTyping', onStopTyping);
      socket.off('userOnline', onUserOnline);
      socket.off('userOffline', onUserOffline);
    };
  }, [socket, userId, fetchConversations]);

  /* ── Helpers ── */
  const formatTime = useCallback((date) => {
    if (!date) return '';
    const diff = Date.now() - new Date(date);
    const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000);
    if (m < 1)  return 'maintenant';
    if (m < 60) return `${m}min`;
    if (h < 24) return `${h}h`;
    if (d < 7)  return `${d}j`;
    return new Date(date).toLocaleDateString('fr-FR', { day:'numeric', month:'short' });
  }, []);

  /* ── Tri + filtre ── */
  const filtered = useMemo(() =>
    [...conversations]
      .sort((a, b) => new Date(b.lastMessage?.createdAt || 0) - new Date(a.lastMessage?.createdAt || 0))
      .filter(c => c.conversationWith?.username?.toLowerCase().includes(searchQuery.toLowerCase())),
    [conversations, searchQuery]
  );

  /* ── Ouvrir chat ── */
  const openChat = useCallback((user) => {
    if (onOpenChat) onOpenChat({ _id: user.id, username: user.username, userPP: user.userPP, socketId: user.socketId });
    navigate(`/chat/${user.id}`);
  }, [onOpenChat, navigate]);

  /* ── Loading ── */
  if (loading) return (
    <>
      <div className={styles.messenger}>
        <div className={styles.header}><h2>Messages</h2></div>
        <div className={styles.skeletonList}>
          {[...Array(5)].map((_, i) => (
            <div key={i} className={styles.skeletonItem}>
              <div className={styles.skeletonAvatar}/>
              <div className={styles.skeletonLines}>
                <div className={styles.skeletonLine} style={{width:'55%'}}/>
                <div className={styles.skeletonLine} style={{width:'80%'}}/>
              </div>
            </div>
          ))}
        </div>
      </div>
      {isMobile && <Menu pp={pp} active={active} setActive={setActive}/>}
    </>
  );

  return (
    <>
      <div className={styles.messenger}>
        {/* Header */}
        <div className={styles.header}>
          <h2>Messages</h2>
          <button
            className={styles.newChatBtn}
            onClick={() => isMobile ? navigate('/friends') : setActive?.('amis')}
            aria-label="Nouveau message"
          >
            <FaEdit />
          </button>
        </div>

        {/* Search */}
        <div className={styles.searchBar}>
          <FaSearch className={styles.searchIcon}/>
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button className={styles.clearBtn} onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>

        {/* Liste */}
        <div className={styles.list}>
          {error ? (
            <div className={styles.emptyState}>
              <FaInbox/>
              <p>Erreur : {error}</p>
              <button className={styles.retryBtn} onClick={fetchConversations}>Réessayer</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={styles.emptyState}>
              <FaInbox/>
              <p>{searchQuery ? 'Aucune conversation trouvée' : 'Aucun message'}</p>
              {!searchQuery && (
                <button className={styles.startBtn} onClick={() => isMobile ? navigate('/friends') : setActive?.('amis')}>
                  Commencer une conversation
                </button>
              )}
            </div>
          ) : (
            filtered.map((conv) => {
              const user     = conv.conversationWith;
              const isUnread = conv.lastMessage?.unread === 1;
              const isOnline = !!user?.socketId;
              return (
                <div
                  key={user?.id}
                  className={`${styles.item} ${isUnread ? styles.unread : ''}`}
                  onClick={() => openChat(user)}
                  role="button"
                  tabIndex={0}
                >
                  {/* Avatar */}
                  <div className={styles.avatarWrap}>
                    {user?.userPP
                      ? <img src={user.userPP} alt={user.username} className={styles.avatar}/>
                      : <div className={styles.avatarPlaceholder}>{user?.username?.charAt(0).toUpperCase()}</div>
                    }
                    {isOnline && <span className={styles.onlineDot}><FaCircle/></span>}
                  </div>

                  {/* Infos */}
                  <div className={styles.info}>
                    <div className={styles.topRow}>
                      <span className={`${styles.name} ${isUnread ? styles.bold : ''}`}>{user?.username}</span>
                      <span className={styles.time}>{formatTime(conv.lastMessage?.createdAt)}</span>
                    </div>
                    <div className={styles.bottomRow}>
                      <span className={`${styles.preview} ${isUnread ? styles.bold : ''}`}>
                        {typingUsers.has(user?.id) ? (
                          <span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>
                            en train d'écrire...
                          </span>
                        ) : (
                          <>
                            {conv.lastMessage?.sender === userId && <span className={styles.you}>Toi : </span>}
                            {conv.lastMessage?.text || '...'}
                          </>
                        )}
                      </span>
                      {isUnread && <span className={styles.badge}/>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {isMobile && !showChat && <Menu pp={pp} active={active} setActive={setActive}/>}
    </>
  );
};

export default Messenger;